import React from 'react';
import { useNavigate } from 'react-router-dom';

import { BaseDraggableWidget } from '../Card';

import useAsync from '../../../hooks/useAsync';

import { getAssets, getLocations, getTickets } from '../../../api';

import {
	classNames,
	createDataTree,
	getBannerHealth,
} from '../../helpers/functions';
import { STATUS_MAPPING } from '../../helpers/constants';

const LabStatus = ({ title, isPreview, ...rest }) => {
	const navigate = useNavigate();
	const { value: locations } = useAsync(getLocations);
	const {
		value: items,
		status,
		error,
		execute: refreshAssets,
	} = useAsync(getAssets, {
		silent: true,
		defaultPayload: {
			pageSize: null,
		},
	});

	const { value: tickets } = useAsync(getTickets, {
		silent: true,
		defaultPayload: { status: ['incoming', 'inprogress'], pageSize: null },
	});

	const locationTree = createDataTree(locations?.rows);

	const overallStatus =
		STATUS_MAPPING[
			getBannerHealth(
				null,
				[],
				locationTree?.map((location) => location.health)
			)
		];

	const stats = [
		{
			name: 'Total Assets',
			value: items?.rows?.length || 0,
			change: '',
			changeType: 'positive',
			onClick: () => navigate('/assets/all'),
		},
		{
			name: 'Open Tickets',
			value: tickets?.rows?.length || 0,
			change: '',
			changeType: 'positive',
			onClick: () => navigate('/tickets'),
		},
	];

	if (isPreview) {
		return (
			<BaseDraggableWidget
				title="Status"
				category="System"
				description={'Quick overview of your system data'}
				{...rest}
				isPreview={true}
			/>
		);
	}
	return (
		<BaseDraggableWidget title={'System Status'} widgetProps={{}} {...rest}>
			<div>
				<h2
					className={classNames(
						'text-xs font-light mb-4 text-gray-500 border-t border-b py-3'
					)}
				>
					{`Current Status: `}
					<span className={classNames(overallStatus?.color, 'font-semibold')}>
						{overallStatus?.label}
					</span>
				</h2>
				<dl className="mx-auto grid grid-cols-1 gap-px sm:grid-cols-2">
					{stats.map((stat) => (
						<div
							key={stat.name}
							className={classNames(
								'flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 bg-white px-4 pb-6 sm:px-6 pt-4',
								typeof stat?.onClick === 'function' ? 'cursor-pointer' : ''
							)}
							onClick={() =>
								typeof stat?.onClick === 'function' ? stat?.onClick() : {}
							}
						>
							<dt className="text-sm font-medium leading-6 text-gray-500">
								{stat.name}
							</dt>
							<dd
								className={classNames(
									stat.changeType === 'negative'
										? 'text-rose-600'
										: 'text-gray-700',
									'text-xs font-medium'
								)}
							>
								{stat.change}
							</dd>
							<dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900">
								{stat.value}
							</dd>
						</div>
					))}
				</dl>

				{locationTree?.map((location) => {
					const statusMapping = STATUS_MAPPING[location.health];

					return (
						<div className="py-1">
							<div
								className={classNames(
									'text-gray-700 group flex items-center px-4 py-2 text-xs cursor-pointer justify-between hover:bg-gray-50'
								)}
								onClick={() => navigate(`/assets/location/${location.id}`)}
							>
								{/* ICON */}
								{location.name}
								<div
									className={classNames(
										statusMapping.color,
										'uppercase tracking-wider text-xxs font-semibold'
									)}
								>
									{statusMapping.label}
								</div>
							</div>

							{location?.children?.map((child) => {
								const childStatusMapping = STATUS_MAPPING[child.health];
								return (
									<div
										className={classNames(
											'text-gray-700 group flex items-center px-4 py-2 text-xs cursor-pointer justify-between pl-8 hover:bg-gray-50'
										)}
										onClick={() => navigate(`/assets/location/${child.id}`)}
									>
										{/* ICON */}
										{child.name}
										<div
											className={classNames(
												childStatusMapping?.color,
												'uppercase tracking-wider text-xxs font-semibold'
											)}
										>
											{childStatusMapping?.label}
										</div>
									</div>
								);
							})}
						</div>
					);
				})}
			</div>
		</BaseDraggableWidget>
	);
};

export default LabStatus;
